import axios from 'axios';
import {
  StockListResponse,
  StockCreateResponse,
  StockTokenResponse,
  CsvImportResponse,
  ManufacturerStock,
  BatchActivateResponse,
  StockStatus,
} from './types';

const API_URL = process.env.REACT_APP_API_URL;

interface ListFilter {
  status?: StockStatus;
  intended_company_id?: number;
  search?: string;
  take?: number;
  skip?: number;
}

// ----- Manufacturer side -----

export async function listStock(filter: ListFilter): Promise<StockListResponse> {
  const params: Record<string, unknown> = {};
  if (filter.status) params.status = filter.status;
  if (filter.intended_company_id) params.intended_company_id = filter.intended_company_id;
  if (filter.search) params.search = filter.search;
  if (filter.take !== undefined) params.take = filter.take;
  if (filter.skip !== undefined) params.skip = filter.skip;
  const { data } = await axios.get<{ data: StockListResponse }>(`${API_URL}/manufacturer/stock`, { params });
  return data.data;
}

export async function createStock(input: {
  manufacturer_id: number;
  bluetooth: string;
  cmb_number: number;
  intended_company_id?: number | null;
  notes?: string | null;
}): Promise<StockCreateResponse> {
  const { data } = await axios.post<{ data: StockCreateResponse }>(`${API_URL}/manufacturer/stock`, input);
  return data.data;
}

export async function importStockCsv(input: { manufacturer_id: number; csv: string }): Promise<CsvImportResponse> {
  const { data } = await axios.post<{ data: CsvImportResponse }>(`${API_URL}/manufacturer/stock/import-csv`, input);
  return data.data;
}

export async function updateStock(id: number, patch: {
  bluetooth?: string;
  cmb_number?: number;
  intended_company_id?: number | null;
  notes?: string | null;
}): Promise<ManufacturerStock> {
  const { data } = await axios.put<{ data: ManufacturerStock }>(`${API_URL}/manufacturer/stock/${id}`, patch);
  return data.data;
}

export async function getStockToken(id: number): Promise<StockTokenResponse> {
  const { data } = await axios.get<{ data: StockTokenResponse }>(`${API_URL}/manufacturer/stock/${id}/token`);
  return data.data;
}

export async function regenerateStockToken(id: number): Promise<StockTokenResponse> {
  const { data } = await axios.post<{ data: StockTokenResponse }>(`${API_URL}/manufacturer/stock/${id}/regen-token`);
  return data.data;
}

export function csvTemplateUrl(): string {
  return `${API_URL}/manufacturer/stock/csv-template`;
}

// ----- Admin side -----

export async function listAdminPendingStock(filter: ListFilter & { manufacturer_id?: number }): Promise<StockListResponse> {
  const params: Record<string, unknown> = {};
  if (filter.status) params.status = filter.status;
  if (filter.manufacturer_id) params.manufacturer_id = filter.manufacturer_id;
  if (filter.intended_company_id) params.intended_company_id = filter.intended_company_id;
  if (filter.search) params.search = filter.search;
  if (filter.take !== undefined) params.take = filter.take;
  if (filter.skip !== undefined) params.skip = filter.skip;
  const { data } = await axios.get<{ data: StockListResponse }>(`${API_URL}/admin/manufacturer-stock`, { params });
  return data.data;
}

export async function activateStock(id: number, company_id: number): Promise<{ stock: ManufacturerStock; box_id: number }> {
  const { data } = await axios.post<{ data: { stock: ManufacturerStock; box_id: number } }>(
    `${API_URL}/admin/manufacturer-stock/${id}/activate`,
    { company_id },
  );
  return data.data;
}

export async function batchActivateStock(items: Array<{ stock_id: number; company_id: number }>): Promise<BatchActivateResponse> {
  const { data } = await axios.post<{ data: BatchActivateResponse }>(
    `${API_URL}/admin/manufacturer-stock/batch-activate`,
    { items },
  );
  return data.data;
}

export async function revokeStock(id: number, reason?: string): Promise<void> {
  await axios.post(`${API_URL}/admin/manufacturer-stock/${id}/revoke`, { reason });
}
